import { ReadonlyVec3, vec3 } from 'gl-matrix';
import { ShaderProgram } from '~/engine/core/utils/ShaderProgram';
import { UniformFloat } from '~/engine/core/utils/uniforms/UniformFloat';
import { UniformInt } from '~/engine/core/utils/uniforms/UniformInt';
import { Grid } from '~/engine/data/common/Grid';
import { BrushStamp, BrushStampContext, LayerDrawer } from './BrushStamp';

/**
 * {@link BrushStamp} of the static brush: for a 2D stroke the SDF of a
 * disc of the brush diameter in the view's plane, one voxel thick; for a
 * 3D stroke the SDF of a ball of that diameter.
 */
export class StaticBrushStamp implements BrushStamp {
    public readonly label = 'Static brush';

    private readonly shader = new BrushDrawShader();

    public constructor(private readonly context: BrushStampContext) {}

    public stampAt(position: ReadonlyVec3, draw: LayerDrawer): void {
        const { image, sliceAxis, strokeMode, diameter, margin } = this.context;
        // Snap a 2D brush along the grid axis the view looks down to the
        // nearest slice center. Without this, clicks whose through-plane
        // position lands on a slice boundary place the brush mesh
        // straddling two slices, and the touch-on-plane behaviour of
        // Edge.intersection treats both adjacent slice planes as
        // crossings — content leaks onto neighbouring slices.
        const center =
            strokeMode === '2d' && sliceAxis !== undefined
                ? image.grid.snapPositionToSliceCenter(position, sliceAxis)
                : position;

        this.shader.start();
        try {
            const total = 2 * margin + diameter;
            this.shader.radius.loadFloat(diameter / total);
            this.shader.margin.loadFloat((2 * margin) / total);
            this.shader.dimensions.loadInt(strokeMode === '3d' ? 3 : 2);
            draw(this.brushBoxAt(center));
        } finally {
            this.shader.stop();
        }
    }

    public delete(): void {
        this.shader.delete();
    }

    /**
     * The brush's box centered on a point: a single voxel spanned by
     * the view's axes, so that its first two axes lie in the displayed plane
     * whichever grid axis that plane cuts, and its texture coordinates run
     * from 0 to 1 between opposite faces.
     * @param center PatientSpace coordinates of the brush center
     */
    private brushBoxAt(center: ReadonlyVec3): Grid {
        const { view, image, strokeMode, diameter, margin } = this.context;
        const size = 2 * margin + diameter;
        // A 3D brush is a ball, so its box is a cube. For a 2D brush the
        // extent along the view normal is the slab a 2D stroke is confined
        // to: one voxel footprint along that normal. A thicker box,
        // combined with the boundary behaviour of Edge.intersection, would
        // make the brush leak onto adjacent slices.
        const sizeNormal =
            strokeMode === '3d'
                ? size
                : image.grid.voxelExtentAlong(view.viewDirection);
        return Grid.centeredOn(
            center,
            view.rotation,
            vec3.fromValues(1, 1, 1),
            vec3.fromValues(size, size, sizeNormal)
        );
    }
}

/**
 * {@link BrushDrawShader} is a {@link ShaderProgram} that is used to draw the SDF of a disc or ball into a brush stroke's draw buffer.
 */
class BrushDrawShader extends ShaderProgram {
    /**
     * Vertex shader source code of the {@link BrushDrawShader}.\
     * It needs the following vertex variables:
     * - position: vec2, position of the vertex relative to the drawObject's texture space. Values in between [-1, 1].
     * - distance: vec3, position of the vertex within the brush box. A value in between [0, 1] on each axis, with the center of the circle at 0.5.
     *
     * It has a single input variable 'dimensions', which is an integer, defining the number of dimensions of the brush.\
     *
     * It has a single output variable, being the 'distanceFromCenter', which is 'distance' mapped to [-1, 1]. Its z component is 0 for a 2D brush.
     */
    private static readonly VERTEX_CONTENT = `#version 300 es
    precision highp float;
    
    in vec2 position;
    in vec3 distance;

    uniform int dimensions;

    out vec3 distanceFromCenter;
    
    void main(){
        gl_Position = vec4(position, 0.0, 1.0);
        distanceFromCenter = distance * 2.0 - 1.0;
        if (dimensions == 2) {
            distanceFromCenter.z = 0.0;
        }

    }`;

    /**
     * Fragment shader source code of the {@link BrushDrawShader}.\
     * It has a single input variable, being the distanceFromCenter, which should be a value in between [-1, 1].\
     * And 2 uniforms:
     * - radius: float, radius of the circle in texture space. A value in between [0, 1].
     * - margin: float, distance of empty space around the circle in texture space. A value in between [0, 1].
     * It outputs a single float color value, being the sdf value of the circle at the given position.
     * @example
     * If brush diameter is 10 mm and margin is 10 mm, the radius should be 5/15 and margin should be 10/15.
     */
    private static readonly FRAGMENT_CONTENT = `#version 300 es
    precision lowp float;

    in vec3 distanceFromCenter;

    out float outColor;

    uniform float radius;
    uniform float margin;

    void main(){
        float distance = length(distanceFromCenter);
        outColor = 0.5 - 0.5 * (distance - radius) / margin;
    }`;

    /**
     * Uniform variable of type float, which defines the radius of the circle in texture space.\
     * This should be a value in between [0, 1].\
     * This is the radius relative to whole texture size. So with 10 mm diameter and 10 mm margin, the radius should be 5/15.
     */
    public readonly radius: UniformFloat;

    /**
     * Uniform variable of type float, which defines the distance of empty space around the circle in texture space.\
     * This should be a value in between [0, 1].\
     * This is the margin relative to whole texture size. So with 10 mm diameter and 10 mm margin, the margin should be 10/15.
     */
    public readonly margin: UniformFloat;

    /**
     * The number of dimensions (2 or 3) of the brush.\
     */
    public readonly dimensions: UniformInt;

    /**
     * Creates a new {@link BrushDrawShader}.\
     * It calls the {@link ShaderProgram.constructor} with the {@link BrushDrawShader.VERTEX_CONTENT} and {@link BrushDrawShader.FRAGMENT_CONTENT} as arguments.\
     * It then creates the {@link BrushDrawShader.radius} and {@link BrushDrawShader.margin} uniforms and stores their locations.
     */
    public constructor() {
        super(
            BrushDrawShader.VERTEX_CONTENT,
            BrushDrawShader.FRAGMENT_CONTENT,
            'position',
            'distance'
        );
        this.radius = new UniformFloat('radius');
        this.margin = new UniformFloat('margin');
        this.dimensions = new UniformInt('dimensions');
        super.storeUniformLocations(this.radius, this.margin, this.dimensions);
    }
}
